import * as React from 'react';
import { ChevronDown, Calendar as CalendarIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Calendar } from './ui/calendar';
import { formatDate } from '../lib/dateFormat';
import { cn } from '../lib/utils';

/**
 * StellarSelect — themed dropdown matching the Stellar dark/gold design system.
 * Replaces native <select>. Use with options=[{value, label}] OR pass <SelectItem> children.
 *
 * Sizing: 'sm' (h-8 text-[11px]) | 'md' (h-9 text-[12px]) — default md.
 */
export function StellarSelect({
  value,
  onValueChange,
  options,
  placeholder = 'Select…',
  className = '',
  size = 'md',
  disabled = false,
  testId,
  children,
}) {
  const sizeClasses =
    size === 'sm'
      ? 'h-8 text-[11px] px-2'
      : 'h-9 text-[12px] px-2.5';

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        data-testid={testId}
        className={cn(
          'bg-card border border-border/30 rounded-lg text-foreground',
          'hover:border-border/50 focus:ring-1 focus:ring-gold/40 focus:border-gold/40',
          'data-[state=open]:border-gold/40 transition-colors',
          'shadow-none',
          sizeClasses,
          className
        )}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent
        className="bg-card border border-border/40 text-foreground shadow-xl z-[100]"
      >
        {children
          ? children
          : (options || []).map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                className="text-[12px] focus:bg-gold/10 focus:text-gold data-[state=checked]:bg-gold/15 data-[state=checked]:text-gold"
              >
                {o.label}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  );
}

/**
 * StellarDatePicker — themed date picker (custom popover + react-day-picker).
 * Replaces native <input type="date">. Value/onChange use ISO YYYY-MM-DD strings.
 *
 * ⚠️ WebKit history — Iter 70.4 (2026-05-18): REMOVED Radix Popover entirely.
 *
 * Real-Safari testing established that under React 19 + WebKit, ANY Radix
 * Popover trigger composition (PopoverTrigger asChild, PopoverAnchor asChild,
 * PopoverAnchor without asChild) silently dropped our React onClick handler
 * on the trigger button. Diagnostic on production Safari:
 *   - 10 real mouse clicks captured at DOM level
 *   - aria-expanded={open} stayed "false" → setState never ran
 *   - portal never mounted
 *
 * Fix (Iter 70.4): drop @radix-ui/react-popover from this component's tree.
 * Use a hand-rolled popover — a sibling <div> absolutely positioned below
 * the trigger, opened by plain `<button onClick>` toggling a `useState`
 * boolean. Click-outside is detected via a document `mousedown` listener
 * mounted in useEffect when open. Escape key handled the same way. The
 * calendar grid (react-day-picker) is kept intact for keyboard/touch nav.
 *
 * Hard rules:
 *   • NO Radix Popover/PopoverTrigger/PopoverAnchor/PopoverContent imports
 *   • NO asChild anywhere on the trigger button
 *   • NO @radix-ui/react-slot composition on the trigger
 *   • NO onPointerDown stopPropagation
 *
 * z-[100] keeps the popover above shadcn Dialog content (z-50).
 * touchAction: manipulation kills the 300ms tap delay on iOS Safari.
 */
export function StellarDatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  className = '',
  size = 'md',
  disabled = false,
  testId,
  minDate, // YYYY-MM-DD
  maxDate, // YYYY-MM-DD
  clearable = true,
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef(null);
  const sizeClasses =
    size === 'sm'
      ? 'h-8 text-[11px] px-2'
      : 'h-9 text-[12px] px-2.5';

  // Parse YYYY-MM-DD as a LOCAL date so the picker doesn't shift by timezone.
  const parseLocalIso = (s) => {
    if (!s) return undefined;
    const [y, m, d] = s.slice(0, 10).split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
  };
  const date = parseLocalIso(value);
  const minD = parseLocalIso(minDate);
  const maxD = parseLocalIso(maxDate);

  // Close on outside click (mousedown fires before click, so we don't race
  // with the trigger's own onClick toggle) + touch + Escape key.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      const root = containerRef.current;
      if (!root) return;
      if (root.contains(e.target)) return; // click inside trigger or calendar → keep open
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocPointer, true);
    document.addEventListener('touchstart', onDocPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer, true);
      document.removeEventListener('touchstart', onDocPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const commit = (d) => {
    if (!d) {
      onChange?.('');
      setOpen(false);
      return;
    }
    // Format as local YYYY-MM-DD (avoid UTC drift from toISOString)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    onChange?.(`${y}-${m}-${day}`);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        style={{ touchAction: 'manipulation' }}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-lg',
          'bg-card border border-border/30 text-foreground',
          'hover:border-border/50 focus:ring-1 focus:ring-gold/40 focus:border-gold/40 focus:outline-none',
          'transition-colors',
          disabled && 'opacity-50 cursor-not-allowed',
          sizeClasses,
          className
        )}
      >
        <span className="flex items-center gap-1.5 truncate">
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
          <span className={cn('truncate', !value && 'text-muted-foreground/40')}>
            {value ? formatDate(value) : placeholder}
          </span>
        </span>
        {clearable && value ? (
          <span
            role="button"
            aria-label="Clear date"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange?.('');
            }}
            className="text-muted-foreground/40 hover:text-foreground text-[14px] leading-none px-1"
          >
            ×
          </span>
        ) : (
          <ChevronDown className="h-3 w-3 text-muted-foreground/40 shrink-0" />
        )}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Choose date"
          className="absolute top-full left-0 mt-1 z-[100] w-auto p-0 bg-card border border-border/40 shadow-xl rounded-md"
          style={{ minWidth: 'max-content' }}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={commit}
            disabled={(d) => {
              if (minD && d < minD) return true;
              if (maxD && d > maxD) return true;
              return false;
            }}
            initialFocus
          />
        </div>
      )}
    </div>
  );
}
